import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import CountUp from '../Counter/count-up-component';
import { getLandingData } from '../../Public/landing-data';
import SystemDataService from '../../Services/SystemDataService';

/**
 * MetricsCard
 * Updated with h-full and min-h for consistent sizing.
 */
const MetricsCard = ({ value, suffix, label, duration }) => (
    <div className="
        bg-white dark:bg-slate-900 
        rounded-[2rem] border-4 border-primary-500 
        p-8 md:p-10
        flex flex-col items-center justify-center 
        transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 group shadow-xl
        w-full h-full min-h-[220px] lg:min-h-[260px]
    ">
        {/* Metric Value - Secondary Gold */}
        <div className="flex items-baseline gap-1 text-secondary-500" dir="ltr">
            <CountUp
                from={0}
                to={value}
                direction="up"
                duration={duration || 2.5}
                className="text-5xl md:text-6xl lg:text-7xl font-black er"
            />
            {suffix && (
                <span className="text-2xl lg:text-3xl font-black">
                    {suffix}
                </span>
            )}
        </div>

        {/* Metric Label - Primary Blue */}
        <p className="mt-4 text-base md:text-lg lg:text-xl text-primary-900 dark:text-gray-100 font-black uppercase  text-center leading-tight">
            {label}
        </p>
    </div>
);

function MetricsSection({ metrics }) {
    const [imageLoaded, setImageLoaded] = useState(false);

    // 1. Access Data & Language
    const systemData = useSelector((state) => state.systemData || {});
    const { businessInfo = {}, dir, language, links = [] } = systemData;
    
    const logoWhite = SystemDataService.byLabel(links, 'Logo White');

    // 2. Get Static Data (Language Aware)
    const landingData = getLandingData(language);
    const { metricsSection } = landingData || {};

    // 3. Determine Metrics Source
    let metricsToDisplay = [];
    if (metrics && metrics.length > 0) {
        metricsToDisplay = metrics;
    } else if (businessInfo.metrics && businessInfo.metrics.length > 0) {
        metricsToDisplay = businessInfo.metrics;
    } else {
        metricsToDisplay = metricsSection?.metrics || [];
    }

    if (!metricsToDisplay.length) return null;

    return (
        <div dir={dir} className="w-full  px-6 py-10 relative overflow-hidden">
        
            <div className="flex flex-col items-center justify-center max-w-7xl mx-auto relative z-10">

                {/* Header with Business Name & Logo */}
                <div className="flex flex-col md:flex-row items-center gap-6 mb-20 animate-fade-in-up">
                    <h2 className="text-4xl md:text-6xl lg:text-7xl font-black  text-white uppercase italic">
                        {businessInfo.businessName || 'ALWAYS ON AIR'}
                    </h2>

                    {logoWhite?.image && (
                        <div className="relative w-52 h-auto lg:w-32 lg:h-32">
                            <img
                                alt="Always On Air Logo"
                                src={logoWhite.image}
                                className={`w-full h-full object-contain transition-opacity duration-700 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                                onLoad={() => setImageLoaded(true)}
                            />
                        </div>
                    )}
                </div>

                {/* Metrics Grid - Added items-stretch for consistent height */}
                <div className="flex flex-wrap justify-center items-stretch gap-6 lg:gap-8 w-full">
                    {metricsToDisplay.map((m, idx) => (
                        <div key={idx} className="flex w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc(25%-1.5rem)]">
                            <MetricsCard
                                value={m.value}
                                suffix={m.suffix}
                                label={m.label}
                                duration={m.duration}
                            />
                        </div>
                    ))}
                </div>

                {/* Disclaimer / Subtext */}
                {metricsSection?.disclaimer && (
                    <p className="text-center text-sm text-gray-300 py-6 max-w-2xl mx-auto opacity-60 font-medium">
                        {metricsSection.disclaimer}
                    </p>
                )}
            </div>
        </div>
    );
}

export default MetricsSection;